
import {
  computed,
  defineComponent, reactive,
  watch,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { IEngenhariaProdutoAcabado } from '@/models/Entidades/Cadastros/PCP/IEngenhariaProduto';
import SelecionarProduto from '@/components/Cadastros/Produtos/SelecionarProduto.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import { useGradeBase } from '@/core/composables/GradeBase';
import { ECustomRenderRow } from '@/core/models/AntDesign/IColumn';
import SelecionarUnidadeProdutoMovimento from '@/components/Cadastros/Produtos/Unidades/SelecionarUnidadeProdutoMovimento.vue';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';

export default defineComponent({
  name: 'LancarMateriaPrimaEtapaProducao',
  props: {
    visivel: {
      type: Boolean,
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    produtoAcabado: {
      type: Array as () => IEngenhariaProdutoAcabado[],
      required: true,
    },
    codigoEtapa: {
      type: Number,
      required: true,
    },
  },
  components: {
    Icone,
    SelecionarProduto,
    SelecionarUnidadeProdutoMovimento,
    CampoNumerico,
  },
  emits: ['salvar', 'update:visivel', 'update:produtoAcabado'],
  setup(props, { emit }) {
    const {
      apresentarMensagemAlerta,
    } = useTelaBase();
    const {
      gradeBase,
    } = useGradeBase();

    const state = reactive({
      editavel: true,
      etapaSelecionada: 0,
      codigoProdutoSelecionado: -1,
      codigoProduto: 0,
      codigoUnidade: 0,
      quantidade: 0,
      descricaoSelecionado: '',
      sigla: '',
      CasasDecimais: 0,
    });

    const computedProdutoAcabado = computed({
      get: () => props.produtoAcabado,
      set: (val: IEngenhariaProdutoAcabado[]) => {
        emit('update:produtoAcabado', val);
      },
    });

    function objetoInicial() {
      state.editavel = true;
      state.codigoProdutoSelecionado = -1;
      state.codigoProduto = 0;
      state.codigoUnidade = 0;
      state.quantidade = 0;
      state.descricaoSelecionado = '';
      state.sigla = '';
    }

    function limparCampos() {
      state.codigoProdutoSelecionado = -1;
      state.codigoProduto = 0;
      state.codigoUnidade = 0;
      state.quantidade = 0;
      state.descricaoSelecionado = '';
      state.sigla = '';
      state.codigoProdutoSelecionado = -1;
    }

    function preencherColunas() {
      gradeBase.colunas = [
        {
          title: 'Código', dataIndex: 'codigo', key: 'codigo', position: 0, visible: false,
        },
        {
          title: 'Produto', dataIndex: 'descricao', key: 'descricao', position: 1, visible: true, width: 300,
        },
        {
          title: 'Unidade', dataIndex: 'siglaUnidade', key: 'siglaUnidade', position: 1, visible: true, width: 75,
        },
        {
          title: 'Quantidade', dataIndex: 'quantidade', key: 'quantidade', position: 1, visible: true, width: 100,
        },
        {
          title: 'Açoes', key: 'acoes', position: 2, visible: true, fixed: 'right', width: 50, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
        },
      ];
    }

    function validaCampos() {
      if (!UtilitarioGeral.validaCodigo(state.codigoProduto)) {
        apresentarMensagemAlerta('Por favor selecione um produto!');
        return false;
      }
      if (!UtilitarioGeral.validaCodigo(state.codigoUnidade)) {
        apresentarMensagemAlerta('Por favor selecione uma unidade!');
        return false;
      }
      if (!UtilitarioGeral.validaNumerico(state.quantidade)) {
        apresentarMensagemAlerta('Por favor informe uma quantidade!');
        return false;
      }
      if (state.codigoProdutoSelecionado === -1 && computedProdutoAcabado.value.some((p) => p.codigoProduto === state.codigoProduto)) {
        apresentarMensagemAlerta('O produto já foi adicionado!');
        return false;
      }
      return true;
    }

    async function atualizarItens(index: number) {
      computedProdutoAcabado.value[index].codigoEtapa = state.etapaSelecionada;
      computedProdutoAcabado.value[index].codigoProduto = state.codigoProduto;
      computedProdutoAcabado.value[index].codigoUnidade = state.codigoUnidade;
      computedProdutoAcabado.value[index].quantidade = state.quantidade;
      computedProdutoAcabado.value[index].descricao = state.descricaoSelecionado;
      computedProdutoAcabado.value[index].siglaUnidade = state.sigla;
      limparCampos();
    }

    async function adicionar(index: number) {
      if (!validaCampos()) { return; }

      if (index > -1) {
        atualizarItens(index);
        return;
      }

      const produtoAcabado: IEngenhariaProdutoAcabado = {} as IEngenhariaProdutoAcabado;

      produtoAcabado.codigoEtapa = state.etapaSelecionada;
      produtoAcabado.codigoProduto = state.codigoProduto;
      produtoAcabado.codigoUnidade = state.codigoUnidade;
      produtoAcabado.quantidade = state.quantidade;
      produtoAcabado.descricao = state.descricaoSelecionado;
      produtoAcabado.siglaUnidade = state.sigla;
      produtoAcabado.ordem = computedProdutoAcabado.value.length + 1;

      computedProdutoAcabado.value.push(produtoAcabado);
      limparCampos();
    }

    function excluir(index: number) {
      computedProdutoAcabado.value.splice(index, 1);
      state.codigoProdutoSelecionado = -1;
    }

    function editar(index: number) {
      state.codigoProdutoSelecionado = index;
      state.codigoProduto = computedProdutoAcabado.value[index].codigoProduto;
      state.quantidade = computedProdutoAcabado.value[index].quantidade;
      state.descricaoSelecionado = computedProdutoAcabado.value[index].descricao;
    }

    watch(() => state.codigoProduto, () => {
      state.codigoUnidade = 0;
      state.sigla = '';
    });

    preencherColunas();

    return {
      props,
      state,
      objetoInicial,
      EPermissaoDados,
      gradeBase,
      ECustomRenderRow,
      editar,
      excluir,
      adicionar,
      computedProdutoAcabado,
      UtilitarioMascara,
    };
  },
});
